import { Badge, Button, cn } from "@z/ui";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { SORT_LABELS, ViewOptions, type ViewOptionsProps } from "./ViewOptions";

export interface ViewHeaderProps extends ViewOptionsProps {
  title: ReactNode;
  count?: number;
  /** Secondary information beside the title, such as project progress. */
  titleMetadata?: ReactNode;
  /** Additional controls beside the View button. */
  actions?: ReactNode;
  /** Project metadata or other context shown below the title. */
  children?: ReactNode;
  className?: string;
}

/** Title, metadata and a single entry point for display preferences. */
export function ViewHeader({
  title,
  count,
  titleMetadata,
  actions,
  children,
  className,
  ...options
}: ViewHeaderProps) {
  const { tags = [], onTagsChange } = options;
  return (
    <header
      data-slot="view-header"
      className={cn("@container flex flex-col gap-3 pb-4", className)}
    >
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <h2 className="flex min-w-0 items-center gap-1 text-[18px] font-semibold tracking-tight text-fg">
            {title}
            {count !== undefined && (
              <span data-slot="view-count" className="tabular-nums">
                ({count})
              </span>
            )}
          </h2>
          {titleMetadata}
        </div>
        <div className="ml-auto flex min-w-0 max-w-full items-center gap-2">
          {actions}
          <ViewOptions {...options} />
        </div>
      </div>
      {children && (
        <div className="flex min-w-0 flex-wrap items-center gap-3 text-fg-muted">{children}</div>
      )}
      {options.displayControls !== false &&
        options.mode !== "board" &&
        options.sort &&
        options.sort.by !== "manual" &&
        options.onSortChange && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-muted">
            <span>
              Sorted by {SORT_LABELS[options.sort.by]}{" "}
              {options.sort.direction === "asc" ? "↑" : "↓"}
            </span>
            <Button
              variant="ghost"
              size="sm"
              title="Switch to manual order to drag tasks"
              onClick={() => options.onSortChange?.({ by: "manual", direction: "asc" })}
            >
              Use manual order
            </Button>
          </div>
        )}
      {tags.length > 0 && (
        <div data-slot="view-tag-filters" className="flex flex-wrap items-center gap-1">
          {tags.map((tag) => (
            <Badge key={tag} variant="accent" data-slot="view-tag-filter" className="pr-0.5">
              {tag}
              {onTagsChange && (
                <button
                  type="button"
                  aria-label={`Remove tag filter ${tag}`}
                  onClick={() =>
                    onTagsChange(tags.filter((entry) => entry.toLowerCase() !== tag.toLowerCase()))
                  }
                  className="inline-flex h-full items-center rounded-sm px-0.5 focus-ring hover:bg-accent/15"
                >
                  <X aria-hidden="true" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </header>
  );
}
